import React from "react";
import BillIcon from "../../assets/BillIcon";
import CompassIcon from "../../assets/CompassIcon";
import CreditCardIcon from "../../assets/CreditCardIcon";
import DashboardCardImage from "../../assets/DashboardCardImage";
import PiggyBankIcon from "../../assets/PiggyBankIcon";
import { useState } from "react";
import { sendMenuClick } from "../../utils/analyticsEvents";

interface RecommendationsDashboardButtonProps {
  format: "large" | "small";
  icon: "piggy-bank" | "credit-card" | "compass" | "bill" | "dashboard-card";
  iconBgColor: string;
  label: string | React.ReactNode;
  onClick: () => void;
}

const RecommendationsDashboardButton: React.FC<
  RecommendationsDashboardButtonProps
> = ({ format, icon, iconBgColor, label, onClick }) => {
  const [isClicked, setIsClicked] = useState(false);

  const isExploreMoreButton = icon === "compass";

  const handleClick = () => {
    let labelText = "";

    if (typeof label === "string") {
      labelText = label;
    } else if (React.isValidElement(label)) {
      if (label.props && label.props.children) {
        if (typeof label.props.children === "string") {
          labelText = label.props.children;
        } else if (Array.isArray(label.props.children)) {
          labelText = label.props.children
            .filter((child: React.ReactNode) => typeof child === "string")
            .join(" ");
        }
      }
    }

    sendMenuClick(labelText);
    if (format === "small") {
      setIsClicked(!isClicked);
    } else {
      setIsClicked(true);
    }
    onClick();
  };

  const getIcon = () => {
    switch (icon) {
      case "piggy-bank":
        return <PiggyBankIcon />;
      case "credit-card":
        return <CreditCardIcon />;
      case "compass":
        return <CompassIcon />;
      case "bill":
        return <BillIcon />;
      case "dashboard-card":
        return <DashboardCardImage />;
    }
  };

  const smallButton = () => {
    return (
      <button
        onClick={handleClick}
        id={`button-${icon}`}
        className={`
          col-span-1 
          flex flex-col 
          items-center 
          border-white 
          border-2 
          ${
            isClicked
              ? "bg-[#F2F4F8]"
              : isExploreMoreButton
                ? "bg-[#EBF4FF]"
                : "bg-white"
          }
          shadow-md shadow-[#527094]/20 hover:shadow-lg hover:shadow-[#527094]/20 transition-colors transition-shadow duration-300 active:bg-[#F2F4F8]
          rounded-[18px]
          p-3 h-[135px] md:h-[148px] 
          md:w-[210px]
          sm:p-4 sm:h-[140px]
          group
        `}
      >
        <div
          className={`ml-0 mr-auto w-12 h-12 rounded-full flex items-center justify-center mb-3
            group-hover:bg-transparent group-active:bg-transparent
          `}
          style={{
            backgroundColor: iconBgColor,
          }}
        >
          <div>{getIcon()}</div>
        </div>
        <span
          className={`text-start my-auto ml-0 mr-auto text-BCP-normal-text font-flexo-demi text-[12px]
            sm:text-[16px]
          `}
        >
          {label}
        </span>
      </button>
    );
  };

  const largeButton = () => {
    return (
      <button
        onClick={handleClick}
        id={`button-${icon}`}
        className={`
          col-span-2 
          flex 
          items-center 
          rounded-[18px] 
          border-white 
          border-2 
          ${
            isClicked
              ? "bg-[#F2F4F8]"
              : isExploreMoreButton
                ? "bg-[#EBF4FF]"
                : "bg-white"
          }
          shadow-md shadow-[#527094]/20 hover:shadow-lg hover:shadow-[#527094]/20 transition-colors transition-shadow duration-300 active:bg-[#F2F4F8]
          p-[16px] h-[135px] md:h-[148px] md:w-[440px] md:p-[40px]
          sm:p-10 sm:h-[130px]
          group
        `}
      >
        <div
          className={`w-23 h-23 rounded-full flex items-center justify-center group-hover:bg-transparent group-active:bg-transparent
            sm:w-16 sm:h-16
          `}
          style={{
            backgroundColor: "transparent",
          }}
        >
          <div className="xl:scale-100 md:scale-100 sm:scale-100 xs:scale-75 2xs:scale-75">
            {getIcon()}
          </div>
        </div>
        <span
          className={`text-start my-auto ml-3 mr-auto text-BCP-normal-text font-flexo-demi text-[14px]
            sm:text-[16px] sm:ml-12 md:ml-12
          `}
        >
          {label}
        </span>
      </button>
    );
  };

  return format === "small" ? smallButton() : largeButton();
};

export default RecommendationsDashboardButton;
